import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { canAccess } from "../hooks/usePermission";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.magnirauniverse.com";

const getUserFromLocal = () => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const parseJwt = (token) => {
  try {
    if (!token) return null;
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

const getNormalizedRole = () => {
  try {
    const user = getUserFromLocal();
    const token = localStorage.getItem("token");
    const decoded = parseJwt(token);

    const roleFromUser = user?.role || "";
    const roleFromToken = decoded?.role || "";

    return String(roleFromUser || roleFromToken || "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "_");
  } catch {
    return "";
  }
};

const getAuthConfig = () => {
  const token = localStorage.getItem("token");
  return {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  };
};

const getProofUrl = (proofPath) => {
  if (!proofPath) return "";

  const raw = String(proofPath).trim();

  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }

  const normalized = raw.replace(/\\/g, "/");

  const uploadsIndex = normalized.toLowerCase().indexOf("/uploads/");
  if (uploadsIndex !== -1) {
    const relativePath = normalized.substring(
      uploadsIndex + "/uploads/".length,
    );
    return `${BASE_URL}/api/files/${relativePath}`;
  }

  if (normalized.startsWith("/api/files/")) {
    return `${BASE_URL}${normalized}`;
  }

  if (normalized.startsWith("/uploads/")) {
    return `${BASE_URL}${normalized}`;
  }

  return `${BASE_URL}/api/files/${normalized.replace(/^\/+/, "")}`;
};

// Income row par proof kisi bhi field naam me aa sakta hai.
const getRowProof = (row) => {
  const proofs = Array.isArray(row?.screenshots)
    ? row.screenshots.filter(Boolean)
    : [];
  return [
    ...new Set(
      [...proofs, row?.screenshot || row?.incomeProof || row?.proof].filter(
        Boolean,
      ),
    ),
  ];
};
const MAX_PROOFS = 5;
const validateProofs = (files) => {
  if (files.some((file) => file.size > 10 * 1024 * 1024)) {
    alert("Each payment proof must be 10MB or less.");
    return false;
  }
  return true;
};

const emptyIncome = {
  date: "",
  amount: "",
  method: "",
  invoiceNo: "",
  taxable: "",
  gstPct: "",
};

const fmtINR = (n) =>
  `₹ ${Number(n || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

const BANK_OR_TAXABLE_METHODS = new Set([
  "NEFT",
  "Cheque",
  "SVB-DBS",
  "SVB-HDFC",
  "SVB-IND",
  "SDC-DBS",
  "SDC-HDFC",
]);

const useWidth = () => {
  const [w, setW] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1280,
  );

  useEffect(() => {
    const onR = () => setW(window.innerWidth);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);

  return w;
};

export default function PaymentDetail({ project, refreshProjects }) {
  const navigate = useNavigate();
  const width = useWidth();

  const isLG = width >= 1200;
  const isMD = width >= 900;
  const isSM = width >= 640;

  const [userRole, setUserRole] = useState(getNormalizedRole());

  const canSeeExpenses =
    canAccess("paymentDetail", "paymentShowExpenseStatus") ||
    canAccess("paymentDetail", "paymentShowExpenseTable") ||
    canAccess("paymentDetail", "paymentShowVendorPayment");

  const canViewPayment = canAccess("paymentDetail", "view");
  const canAddIncome = canAccess("paymentDetail", "create");
  const canEditIncome = canAccess("paymentDetail", "edit");
  const canDeleteIncome = userRole === "admin";

  const canShowProjectService = canAccess(
    "paymentDetail",
    "paymentShowProjectService",
  );

  const canEditProjectService = canAccess(
    "paymentDetail",
    "paymentEditProjectService",
  );

  const canShowIncomeStatus = canAccess(
    "paymentDetail",
    "paymentShowIncomeStatus",
  );

  const canShowExpenseStatus = canAccess(
    "paymentDetail",
    "paymentShowExpenseStatus",
  );

  const canShowActualBalance = canAccess(
    "paymentDetail",
    "paymentShowActualBalance",
  );

  const canShowCustomerSO = canAccess("paymentDetail", "paymentShowCustomerSO");

  const canShowIncomeTable = canAccess(
    "paymentDetail",
    "paymentShowIncomeTable",
  );

  const canShowExpenseTable = canAccess(
    "paymentDetail",
    "paymentShowExpenseTable",
  );

  const canShowTaxableValue = canAccess(
    "paymentDetail",
    "paymentShowTaxableValue",
  );

  const canShowGST = canAccess("paymentDetail", "paymentShowGST");

  const canShowInvoiceAmount = canAccess(
    "paymentDetail",
    "paymentShowInvoiceAmount",
  );

  const canShowPendingClient = canAccess(
    "paymentDetail",
    "paymentShowPendingClient",
  );

  const canShowVendorPayment = canAccess(
    "paymentDetail",
    "paymentShowVendorPayment",
  );

  const [approvedAmount, setApprovedAmount] = useState(0);
  const [vendorPOTotal, setVendorPOTotal] = useState(0);

  const [designer, setDesigner] = useState("");
  const [salesManager, setSalesManager] = useState("");

  const [handoverDate, setHandoverDate] = useState("");
  const [workStartDate, setWorkStartDate] = useState("");
  const [completionDate, setCompletionDate] = useState("");

  const [siteService, setSiteService] = useState(false);
  const [dcSiteService, setDcSiteService] = useState(false);
  const [governmentService, setGovernmentService] = useState(false);
  const [cityStore, setCityStore] = useState("");

  const [metaSaving, setMetaSaving] = useState(false);

  const [quotes, setQuotes] = useState([]);
  const [incomeRows, setIncomeRows] = useState([]);
  const [income, setIncome] = useState({ ...emptyIncome });
  const [incomeProof, setIncomeProof] = useState([]);

  const [paidExpenseRows, setPaidExpenseRows] = useState([]);

  const [editingId, setEditingId] = useState(null);
  const [editIncome, setEditIncome] = useState({
    date: "",
    amount: "",
    method: "",
    invoiceNo: "",
    gstRate: 18,
  });

  // Edit karte time naya payment proof choose karne ke liye.
  // null rahe to purana proof DB me waise ka waisa rehta hai.
  const [editProof, setEditProof] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);

  const fmtYMD = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");

  useEffect(() => {
    const refreshRole = () => setUserRole(getNormalizedRole());

    refreshRole();
    window.addEventListener("storage", refreshRole);
    window.addEventListener("focus", refreshRole);

    return () => {
      window.removeEventListener("storage", refreshRole);
      window.removeEventListener("focus", refreshRole);
    };
  }, []);

  const loadPaidVendorPayments = async (projectName) => {
    try {
      const [paidRes, requestRes] = await Promise.all([
        axios.get(
          `${BASE_URL}/api/vendor-payments?projectName=${encodeURIComponent(
            projectName,
          )}`,
          getAuthConfig(),
        ),
        axios
          .get(
            `${BASE_URL}/api/vendor-payment-requests?projectName=${encodeURIComponent(
              projectName,
            )}`,
            getAuthConfig(),
          )
          .catch(() => ({ data: [] })),
      ]);

      const paidRows = Array.isArray(paidRes.data) ? paidRes.data : [];
      const requestRows = Array.isArray(requestRes.data) ? requestRes.data : [];

      const mergedRows = paidRows.map((paid) => {
        if (paid.paymentProof || paid.proof || paid.screenshot) {
          return paid;
        }

        const matchedRequest = requestRows.find((request) => {
          const directIdMatch =
            String(request?._id || "") === String(paid?.requestId || "") ||
            String(request?._id || "") ===
              String(paid?.paymentRequestId || "") ||
            String(request?.vendorPaymentId || "") === String(paid?._id || "");

          if (directIdMatch) return true;

          const sameVendor =
            String(request?.vendorId || "") === String(paid?.vendorId || "");

          const sameAmount =
            Number(request?.paymentAmt || 0) === Number(paid?.paymentAmt || 0);

          const paidStatus =
            String(request?.status || "").toUpperCase() === "PAID";

          return sameVendor && sameAmount && paidStatus;
        });

        return {
          ...paid,
          paymentProof:
            matchedRequest?.paymentProof ||
            matchedRequest?.proof ||
            matchedRequest?.screenshot ||
            "",
        };
      });

      setPaidExpenseRows(mergedRows);
    } catch (error) {
      console.error("Paid vendor payments fetch error:", error);
      setPaidExpenseRows([]);
    }
  };

  const loadVendorPOTotal = async (projectName) => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/vendor-po?projectName=${encodeURIComponent(projectName)}`,
        getAuthConfig(),
      );

      const poList = Array.isArray(res.data) ? res.data : [res.data];

      const total = poList.reduce((sum, po) => {
        if (!po) return sum;

        if (po.totalAmount !== undefined && po.totalAmount !== null) {
          return sum + Number(po.totalAmount || 0);
        }

        const itemTotal = (po.items || []).reduce(
          (s, item) => s + Number(item.vendorAmount || 0),
          0,
        );

        return sum + itemTotal;
      }, 0);

      setVendorPOTotal(total);
    } catch (err) {
      console.error("Vendor PO total fetch error:", err);
      setVendorPOTotal(0);
    }
  };

  const reloadIncomeRows = async () => {
    const res = await axios.get(
      `${BASE_URL}/api/payments?projectName=${encodeURIComponent(
        project.name,
      )}`,
      getAuthConfig(),
    );

    setIncomeRows(res.data || []);
  };

  useEffect(() => {
    if (!project?.name) return;

    const projectName = project.name.trim();

    axios
      .get(
        `${BASE_URL}/api/quotes?projectName=${encodeURIComponent(projectName)}`,
        getAuthConfig(),
      )
      .then((res) => {
        const list = res.data || [];
        setQuotes(list);
        const approved = list.find((q) => q.status === "Approved");
        setApprovedAmount(approved?.finalAmount || 0);
      })
      .catch(() => {
        setQuotes([]);
        setApprovedAmount(0);
      });

    axios
      .get(
        `${BASE_URL}/api/payments?projectName=${encodeURIComponent(projectName)}`,
        getAuthConfig(),
      )
      .then((res) => setIncomeRows(res.data || []))
      .catch(() => setIncomeRows([]));

    if (canShowExpenseStatus || canShowExpenseTable || canShowVendorPayment) {
      loadPaidVendorPayments(projectName);
      loadVendorPOTotal(projectName);
    }

    axios
      .get(`${BASE_URL}/api/project-meta`, {
        params: { projectId: project._id, projectName },
        ...getAuthConfig(),
      })
      .then((res) => {
        const m = res.data;

        const toBool = (v) =>
          v === true ||
          v === 1 ||
          v === "1" ||
          String(v).toUpperCase() === "YES" ||
          String(v).toUpperCase() === "TRUE" ||
          String(v).toUpperCase() === "Y";

        if (m) {
          setDesigner(m.designer || "");
          setSalesManager(m.salesManager || "");
          setHandoverDate(m.handoverDate ? fmtYMD(m.handoverDate) : "");
          setWorkStartDate(m.workStartDate ? fmtYMD(m.workStartDate) : "");
          setCompletionDate(m.completionDate ? fmtYMD(m.completionDate) : "");
          setSiteService(toBool(m.siteService));
          setDcSiteService(toBool(m.dcSiteService));
          setGovernmentService(toBool(m.governmentService));
          setCityStore(m.cityStore || "");
        }
      })
      .catch(() => {});
  }, [
    project?._id,
    project?.name,
    canShowExpenseStatus,
    canShowExpenseTable,
    canShowVendorPayment,
  ]);

  useEffect(() => {
    const method = income.method;
    const amt = Number(income.amount || 0);

    if (BANK_OR_TAXABLE_METHODS.has(method)) {
      const nextGst = 18;
      const nextTaxable = amt ? Math.round((amt / 1.18) * 100) / 100 : "";

      if (income.gstPct !== nextGst || income.taxable !== nextTaxable) {
        setIncome((prev) => ({
          ...prev,
          gstPct: nextGst,
          taxable: nextTaxable,
        }));
      }
    } else {
      if (income.gstPct || income.taxable) {
        setIncome((prev) => ({ ...prev, gstPct: "", taxable: "" }));
      }
    }
  }, [income.method, income.amount]);

  const totals = useMemo(() => {
    const received = incomeRows.reduce((s, r) => s + Number(r.amount || 0), 0);

    const spent = paidExpenseRows.reduce(
      (s, r) => s + Number(r.paymentAmt || 0),
      0,
    );

    const pendingVendorPayment = Math.max(vendorPOTotal - spent, 0);
    const pendingFromClient = Math.max(approvedAmount - received, 0);

    return {
      received,
      spent,
      vendorQuoteTotal: vendorPOTotal,
      pendingVendorPayment,
      pendingFromClient,
    };
  }, [incomeRows, paidExpenseRows, vendorPOTotal, approvedAmount]);

  const paymentRatio = approvedAmount
    ? Math.min(100, (totals.received / approvedAmount) * 100)
    : 0;

  const expenseRatio = totals.vendorQuoteTotal
    ? Math.min(100, (totals.spent / totals.vendorQuoteTotal) * 100)
    : 0;

  const pendingAmt = Math.max(approvedAmount - totals.received, 0);
  const actualBalanceOnHand =
    Number(totals.received || 0) - Number(totals.spent || 0);

  const saveHeaderMeta = async () => {
    if (!canEditProjectService) return;

    setMetaSaving(true);

    try {
      const payload = {
        projectId: project._id,
        projectName: project.name,
        designer: designer || "",
        salesManager: salesManager || "",
        handoverDate: handoverDate || null,
        workStartDate: workStartDate || null,
        completionDate: completionDate || null,
        siteService: siteService ? "YES" : "NO",
        dcSiteService: dcSiteService ? "YES" : "NO",
        governmentService: governmentService ? "YES" : "NO",
        cityStore,
      };

      await axios.post(
        `${BASE_URL}/api/project-meta/set`,
        payload,
        getAuthConfig(),
      );
    } catch {
      alert("Failed to save header/service details.");
    } finally {
      setMetaSaving(false);
    }
  };

  const addIncome = async () => {
    if (!canAddIncome) return;

    try {
      if (!income.date || !income.amount || !income.method) {
        alert("Please fill Date, Amount and Payment Method for income.");
        return;
      }

      const fd = new FormData();
      fd.append("projectName", project.name);
      fd.append("projectId", project._id);
      fd.append("date", income.date);
      fd.append("amount", income.amount);
      fd.append("method", income.method);
      fd.append("invoiceNo", income.invoiceNo || "");
      fd.append("salesManager", salesManager || "");
      fd.append("designerName", designer || "");

      if (BANK_OR_TAXABLE_METHODS.has(income.method)) {
        fd.append("gstRate", 18);
      }

      incomeProof.forEach((file) => fd.append("screenshot", file));

      await axios.post(`${BASE_URL}/api/payments/save`, fd, {
        ...getAuthConfig(),
        headers: {
          ...getAuthConfig().headers,
          "Content-Type": "multipart/form-data",
        },
      });

      await reloadIncomeRows();

      setIncome({ ...emptyIncome });
      setIncomeProof([]);
      refreshProjects && refreshProjects();
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to add income.");
    }
  };

  const previewTax = (amount, method, rate = 18) => {
    const amt = Number(amount || 0);

    if (BANK_OR_TAXABLE_METHODS.has(method)) {
      const taxable = Math.round((amt / (1 + rate / 100)) * 100) / 100;
      const gst = Math.round((amt - taxable) * 100) / 100;
      return { taxable, gst };
    }

    return { taxable: 0, gst: 0 };
  };

  const beginEdit = (row) => {
    if (!canEditIncome) return;

    setEditingId(row._id);
    setEditProof([]);
    setEditIncome({
      date: fmtYMD(row.date),
      amount: row.amount,
      method: row.method,
      invoiceNo: row.invoiceNo || "",
      gstRate: row.gstRate || 18,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditProof([]);
    setEditIncome({
      date: "",
      amount: "",
      method: "",
      invoiceNo: "",
      gstRate: 18,
    });
  };

  const handleEditProofChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!validateProofs(files)) return;
    const current = getRowProof(incomeRows.find((r) => r._id === editingId));
    if (current.length + editProof.length + files.length > MAX_PROOFS) {
      alert(`Maximum ${MAX_PROOFS} payment proofs allowed per entry.`);
      return;
    }
    setEditProof((previous) => [...previous, ...files]);
  };

  const saveEdit = async (id) => {
    if (!canEditIncome) return;

    try {
      setSavingEdit(true);

      const fd = new FormData();
      fd.append("date", editIncome.date);
      fd.append("amount", editIncome.amount);
      fd.append("method", editIncome.method);
      fd.append("invoiceNo", editIncome.invoiceNo || "");

      if (BANK_OR_TAXABLE_METHODS.has(editIncome.method)) {
        fd.append("gstRate", editIncome.gstRate || 18);
      }

      // Naya file choose kiya ho tabhi bhejo.
      // File na bheje to backend purana proof rakhta hai.
      editProof.forEach((file) => fd.append("screenshot", file));

      await axios.put(`${BASE_URL}/api/payments/${id}`, fd, {
        ...getAuthConfig(),
        headers: {
          ...getAuthConfig().headers,
          "Content-Type": "multipart/form-data",
        },
      });

      await reloadIncomeRows();

      cancelEdit();
      refreshProjects && refreshProjects();
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to update payment.");
    } finally {
      setSavingEdit(false);
    }
  };

  const removeIncome = async (id) => {
    if (!canDeleteIncome) return;

    try {
      if (!window.confirm("Admin: Delete this income entry permanently?"))
        return;

      await axios.delete(`${BASE_URL}/api/payments/${id}`, getAuthConfig());

      await reloadIncomeRows();

      refreshProjects && refreshProjects();
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to delete payment.");
    }
  };

  const B = "#4d7bd6";
  const line = "1px solid #bcd3ff";

  const headerGridCols = isLG
    ? "2fr 1fr 1fr auto"
    : isMD
      ? "1.6fr 1fr 1fr auto"
      : "1fr";

  const dateGridCols = isMD
    ? "repeat(3, 1fr)"
    : isSM
      ? "repeat(2, 1fr)"
      : "1fr";

  const rowAddWrapIncome = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 170px), 1fr))",
    gap: "10px",
    alignItems: "center",
    width: "100%",
    marginBottom: 12,
    boxSizing: "border-box",
  };

  const scrollWrap = (minWidth = 800) => ({
    width: "100%",
    overflowX: "auto",
    borderRadius: 6,
  });

  const tableStyle = (minWidth = 800) => ({
    width: "100%",
    minWidth,
    borderCollapse: "collapse",
    border: line,
  });

  const hasActionColumn = canEditIncome || canDeleteIncome;

  const incomeColumnCount =
    6 +
    (canShowTaxableValue ? 1 : 0) +
    (canShowGST ? 1 : 0) +
    (canShowInvoiceAmount ? 1 : 0) +
    (hasActionColumn ? 1 : 0);

  if (!canViewPayment) {
    return (
      <div style={{ padding: 30, color: "red", fontWeight: 700 }}>
        You do not have permission to view payment details.
      </div>
    );
  }

  return (
    <div
      style={{
        padding: isSM ? 24 : 14,
        background: "#f5f7fd",
        fontFamily: "Calibri, Segoe UI, sans-serif",
        fontSize: 16,
        width: "100%",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "relative",
          borderBottom: "1px solid #dbeafe",
          paddingBottom: "10px",
          marginBottom: "16px",
        }}
      >
        <button
          onClick={() => navigate("/project")}
          style={{
            background: "white",
            color: "#000",
            padding: "6px 14px",
            border: "1px solid #ccc",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "600",
          }}
        >
          ← Back
        </button>

        <h2
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            margin: 0,
            fontSize: "20px",
            fontWeight: "700",
            color: "#000",
          }}
        >
          Account Summary
        </h2>

        <div style={{ width: "100px" }} />
      </div>

      {canShowProjectService && (
        <Block title="PROJECT / SERVICE DETAILS" color={B} borderColor={B}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: headerGridCols,
              gap: 12,
              alignItems: "end",
              marginBottom: 12,
            }}
          >
            <div>
              <label style={labelStyle}>PROJECT NAME</label>
              <input
                type="text"
                value={project?.name || ""}
                readOnly
                style={readOnlyInput}
              />
            </div>

            <div>
              <label style={labelStyle}>DC DESIGNER</label>
              <select
                value={designer}
                onChange={(e) => setDesigner(e.target.value)}
                style={inpBox}
                disabled={!canEditProjectService}
              >
                <option value="">Select Designer</option>
                <option value="Krishna Sharma">Krishna Sharma</option>
                <option value="Khushal Ajagiya">Khushal Ajagiya</option>
                <option value="Pranav Patel">Pranav Patel</option>
                <option value="Yesha">Yesha</option>
                <option value="Jay">Jay</option>
              </select>
            </div>

            <div>
              <label style={labelStyle}>DC SALES MANAGER</label>
              <select
                value={salesManager}
                onChange={(e) => setSalesManager(e.target.value)}
                style={inpBox}
                disabled={!canEditProjectService}
              >
                <option value="">Select Sales Manager</option>
                <option value="Shivam Pandey">Shivam Pandey</option>
                <option value="Param">Param</option>
                <option value="Vijay">Vijay</option>
              </select>
            </div>

            {canEditProjectService && (
              <button
                onClick={saveHeaderMeta}
                style={btnBlue}
                disabled={metaSaving}
              >
                {metaSaving ? "Saving..." : "Save Details"}
              </button>
            )}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: 12,
            }}
          >
            <CheckboxField
              label="SITE SERVICE"
              checked={!!siteService}
              onChange={setSiteService}
              disabled={!canEditProjectService}
            />
            <CheckboxField
              label="DC SITE SERVICE"
              checked={!!dcSiteService}
              onChange={setDcSiteService}
              disabled={!canEditProjectService}
            />
            <CheckboxField
              label="GOVERNMENT SERVICE"
              checked={!!governmentService}
              onChange={setGovernmentService}
              disabled={!canEditProjectService}
            />
            <div style={{ width: "220px", minWidth: "220px" }}>
              <Select
                value={cityStore}
                onChange={setCityStore}
                options={["Ahmedabad", "Surat", "Gandhinagar"]}
                placeholder="Select city/store"
                disabled={!canEditProjectService}
              />
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: dateGridCols,
              gap: 12,
            }}
          >
            <Field
              label="HANDOVER DATE"
              type="date"
              value={handoverDate}
              onChange={setHandoverDate}
              readOnly={!canEditProjectService}
            />
            <Field
              label="WORK START DATE"
              type="date"
              value={workStartDate}
              onChange={setWorkStartDate}
              readOnly={!canEditProjectService}
            />
            <Field
              label="COMPLETION DATE"
              type="date"
              value={completionDate}
              onChange={setCompletionDate}
              readOnly={!canEditProjectService}
            />
          </div>
        </Block>
      )}

      {(canShowIncomeStatus || (canSeeExpenses && canShowExpenseStatus)) && (
        <div
          style={{
            display: "flex",
            gap: "20px",
            marginTop: 20,
            flexDirection: isMD ? "row" : "column",
          }}
        >
          {canShowIncomeStatus && (
            <div
              style={{
                ...timelineCard,
                flex: 1,
                fontSize: 18,
                lineHeight: 1.6,
              }}
            >
              <div style={{ ...sectionTitle, fontSize: 26 }}>Income Status</div>

              <div style={metaRow3}>
                <span style={metaItem}>
                  Sales Order (SO):{" "}
                  <b style={{ fontSize: 20 }}>{fmtINR(approvedAmount)}</b>
                </span>

                <span
                  style={{
                    ...metaItem,
                    color: "#166534",
                    fontWeight: 900,
                    textAlign: "center",
                  }}
                >
                  Collected:{" "}
                  <b style={{ fontSize: 20 }}>{fmtINR(totals.received)}</b>
                </span>

                {canShowPendingClient && (
                  <span style={{ ...metaItem, textAlign: "right" }}>
                    Pending Collection:{" "}
                    <b style={{ fontSize: 20 }}>{fmtINR(pendingAmt)}</b>
                  </span>
                )}
              </div>

              <div style={barBg}>
                <div style={barFillBlue(paymentRatio)} />
              </div>
            </div>
          )}

          {canSeeExpenses && canShowExpenseStatus && (
            <div
              style={{
                ...timelineCard,
                flex: 1,
                fontSize: 18,
                lineHeight: 1.6,
              }}
            >
              <div style={{ ...sectionTitle, fontSize: 26 }}>
                Expense Status
              </div>

              <div style={metaRow3}>
                <span style={metaItem}>
                  Vendor PO Total:{" "}
                  <b style={{ fontSize: 20, color: "#7c2d12" }}>
                    {fmtINR(totals.vendorQuoteTotal)}
                  </b>
                </span>

                <span
                  style={{
                    ...metaItem,
                    color: "#ea580c",
                    fontWeight: 900,
                    textAlign: "center",
                  }}
                >
                  Expense done till date:{" "}
                  <b style={{ fontSize: 20 }}>{fmtINR(totals.spent)}</b>
                </span>

                {canShowVendorPayment && (
                  <span style={{ ...metaItem, textAlign: "right" }}>
                    Pending Payment:{" "}
                    <b style={{ fontSize: 20 }}>
                      {fmtINR(totals.pendingVendorPayment)}
                    </b>
                  </span>
                )}
              </div>

              <div style={barBg}>
                <div style={barFillOrange(expenseRatio)} />
              </div>
            </div>
          )}
        </div>
      )}

      {canShowActualBalance && (
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              marginTop: 16,
              padding: "14px 18px",
              border: "2px solid #16a34a",
              borderRadius: 12,
              background: "#f0fdf4",
              textAlign: "center",
              fontSize: 22,
              fontWeight: 900,
              color: actualBalanceOnHand >= 0 ? "#166534" : "#dc2626",
              display: "inline-block",
              minWidth: "420px",
            }}
          >
            Actual Balance on Hand: {fmtINR(actualBalanceOnHand)}
          </div>
        </div>
      )}

      <br />

      {canShowCustomerSO && (
        <Block title="Customer Estimate/Sales Order" color={B}>
          <div style={scrollWrap(720)}>
            <table style={tableStyle(720)}>
              <thead>
                <tr style={{ background: "#eaf1ff" }}>
                  {["#", "DATE", "FINAL AMOUNT", "STATUS"].map((h) => (
                    <th key={h} style={th(line)}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {quotes.map((q, i) => (
                  <tr
                    key={q._id || i}
                    style={{
                      background:
                        q.status === "Approved" ? "#FFFFC5" : "transparent",
                    }}
                  >
                    <td style={td(line, "center")}>{i + 1}</td>
                    <td style={td(line)}>
                      {q.date
                        ? new Date(q.date).toLocaleDateString("en-GB")
                        : "-"}
                    </td>
                    <td style={td(line, "right")}>
                      ₹ {Number(q.finalAmount || 0).toLocaleString("en-IN")}
                    </td>
                    <td style={td(line, "center")}>
                      <span
                        style={{
                          padding: "4px 10px",
                          borderRadius: 999,
                          fontWeight: 800,
                          fontSize: 14,
                          color:
                            q.status === "Approved" ? "#0f5132" : "#842029",
                          background:
                            q.status === "Approved" ? "#d1e7dd" : "#f8d7da",
                          border: `1px solid ${
                            q.status === "Approved" ? "#badbcc" : "#f5c2c7"
                          }`,
                        }}
                      >
                        {q.status === "Approved"
                          ? "Approved (Final Sales Order)"
                          : q.status || "-"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Block>
      )}

      {canShowIncomeTable && (
        <Block
          title="INCOME"
          color="#ccf2cc"
          titleColor="#000"
          borderColor="#ccf2cc"
        >
          <div
            style={{
              backgroundColor: "#e6f9e6",
              padding: 12,
              borderRadius: 6,
              fontSize: 18,
              lineHeight: 1.6,
            }}
          >
            {canAddIncome && (
              <div style={rowAddWrapIncome}>
                <Input
                  placeholder="Date"
                  type="date"
                  value={income.date}
                  onChange={(v) => setIncome({ ...income, date: v })}
                />

                <Input
                  placeholder="Amt Received"
                  type="number"
                  value={income.amount}
                  onChange={(v) => setIncome({ ...income, amount: v })}
                />

                <Select
                  value={income.method}
                  onChange={(v) => setIncome({ ...income, method: v })}
                  options={[
                    "NEFT",
                    "SVB-DBS",
                    "Cash",
                    "Cheque",
                    "SVB-HDFC",
                    "SVB-IND",
                    "SDC-DBS",
                    "SDC-HDFC",
                    "UPI",
                  ]}
                  placeholder="Mode of Payment"
                />

                <Input
                  placeholder="Invoice No."
                  value={income.invoiceNo}
                  onChange={(v) => setIncome({ ...income, invoiceNo: v })}
                />

                {canShowTaxableValue && (
                  <input
                    value={income.taxable}
                    readOnly
                    placeholder="Taxable Value"
                    style={{ ...inpBox, background: "#f6f8ff" }}
                  />
                )}

                {canShowGST && (
                  <input
                    value={income.gstPct}
                    readOnly
                    placeholder="GST %"
                    style={{ ...inpBox, background: "#f6f8ff" }}
                  />
                )}

                <div style={{ minWidth: 0, width: "100%", maxWidth: "100%" }}>
                  <label
                    style={{
                      ...inpBox,
                      display: "flex",
                      alignItems: "center",
                      minHeight: 42,
                      width: "100%",
                      maxWidth: "100%",
                      boxSizing: "border-box",
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      cursor: "pointer",
                      fontSize: 13,
                      lineHeight: 1.3,
                    }}
                  >
                    📎 Income Proof (optional: {incomeProof.length}/5)
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,.gif"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        e.target.value = "";
                        if (!validateProofs(files)) return;
                        if (incomeProof.length + files.length > MAX_PROOFS) {
                          alert("Maximum 5 payment proofs allowed.");
                          return;
                        }
                        setIncomeProof((old) => [...old, ...files]);
                      }}
                    />
                  </label>
                  {incomeProof.map((file, index) => (
                    <div key={`${file.name}-${index}`} style={{ fontSize: 12 }}>
                      {file.name}{" "}
                      <button
                        type="button"
                        onClick={() =>
                          setIncomeProof((old) =>
                            old.filter((_, i) => i !== index),
                          )
                        }
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>

                <button style={addBtn} onClick={addIncome}>
                  Add
                </button>
              </div>
            )}

            <style>{`
              .compact-income-table th { padding: 8px 4px !important; font-size: 14px !important; line-height: 1.25; }
              .compact-income-table td { padding: 7px 4px !important; font-size: 14px !important; line-height: 1.35; overflow-wrap: anywhere; }
              .compact-income-table input, .compact-income-table select { max-width: 100%; min-width: 0; font-size: 13px !important; padding: 7px 4px !important; box-sizing: border-box; }
              .compact-income-table a { padding: 4px 6px !important; font-size: 12px !important; }
            `}</style>
            <div style={scrollWrap(1060)}>
              <table
                className="compact-income-table"
                style={{
                  ...tableStyle(1060),
                  tableLayout: "fixed",
                  backgroundColor: "#f9fff9",
                  fontSize: 14,
                  textAlign: "center",
                }}
              >
                <colgroup>
                  <col style={{ width: "5%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "15%" }} />
                  {canShowTaxableValue && <col style={{ width: "11%" }} />}
                  {canShowGST && <col style={{ width: "10%" }} />}
                  {canShowInvoiceAmount && <col style={{ width: "10%" }} />}
                  {hasActionColumn && <col style={{ width: "8%" }} />}
                </colgroup>
                <thead>
                  <tr style={{ background: "#d6f5d6" }}>
                    {[
                      "SR NO",
                      "DATE",
                      "AMT RECEIVED",
                      "MODE OF PAYMENT",
                      "INVOICE NO.",
                      "INCOME PROOF",
                      ...(canShowTaxableValue ? ["TAXABLE VALUE"] : []),
                      ...(canShowGST ? ["GST"] : []),
                      ...(canShowInvoiceAmount ? ["INVOICE AMT"] : []),
                      ...(hasActionColumn ? ["ACTION"] : []),
                    ].map((h) => (
                      <th key={h} style={{ ...th(line), textAlign: "center" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody style={{ backgroundColor: "#ccf2cc" }}>
                  {incomeRows.map((r, idx) => {
                    const isEdit = editingId === r._id;
                    const existingProofs = getRowProof(r);

                    if (isEdit) {
                      const { taxable, gst } = previewTax(
                        editIncome.amount,
                        editIncome.method,
                        editIncome.gstRate,
                      );

                      return (
                        <tr key={r._id}>
                          <td style={td(line, "center")}>{idx + 1}</td>
                          <td style={td(line, "center")}>
                            <input
                              type="date"
                              value={editIncome.date}
                              onChange={(e) =>
                                setEditIncome({
                                  ...editIncome,
                                  date: e.target.value,
                                })
                              }
                              style={inpBox}
                            />
                          </td>
                          <td style={td(line, "center")}>
                            <input
                              type="number"
                              value={editIncome.amount}
                              onChange={(e) =>
                                setEditIncome({
                                  ...editIncome,
                                  amount: e.target.value,
                                })
                              }
                              style={inpBox}
                            />
                          </td>
                          <td style={td(line, "center")}>
                            <select
                              value={editIncome.method}
                              onChange={(e) =>
                                setEditIncome({
                                  ...editIncome,
                                  method: e.target.value,
                                })
                              }
                              style={inpBox}
                            >
                              {[
                                "NEFT",
                                "SVB-DBS",
                                "Cash",
                                "Cheque",
                                "SVB-HDFC",
                                "SVB-IND",
                                "SDC-DBS",
                                "SDC-HDFC",
                                "UPI",
                              ].map((m) => (
                                <option key={m} value={m}>
                                  {m}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td style={td(line, "center")}>
                            <input
                              value={editIncome.invoiceNo}
                              onChange={(e) =>
                                setEditIncome({
                                  ...editIncome,
                                  invoiceNo: e.target.value,
                                })
                              }
                              style={inpBox}
                            />
                          </td>

                          <td style={td(line, "center")}>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 6,
                              }}
                            >
                              {existingProofs.map((proof, index) => (
                                <a
                                  key={proof}
                                  href={getProofUrl(proof)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={proofBtnStyle}
                                >
                                  View Proof {index + 1}
                                </a>
                              ))}
                              <label
                                style={{ ...proofBtnStyle, cursor: "pointer" }}
                              >
                                📎 Add Proof (
                                {existingProofs.length + editProof.length}/5)
                                <input
                                  type="file"
                                  multiple
                                  accept=".pdf,.jpg,.jpeg,.png,.gif"
                                  style={{ display: "none" }}
                                  onChange={handleEditProofChange}
                                />
                              </label>
                              {editProof.map((file, index) => (
                                <div
                                  key={`${file.name}-${index}`}
                                  style={{ fontSize: 12 }}
                                >
                                  {file.name}{" "}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setEditProof((old) =>
                                        old.filter((_, i) => i !== index),
                                      )
                                    }
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          </td>

                          {canShowTaxableValue && (
                            <td style={td(line, "center")}>
                              {taxable
                                ? `₹ ${taxable.toLocaleString("en-IN")}`
                                : "-"}
                            </td>
                          )}
                          {canShowGST && (
                            <td
                              style={{
                                ...td(line, "center"),
                                whiteSpace: "nowrap",
                                minWidth: 125,
                              }}
                            >
                              {gst ? `₹ ${gst.toLocaleString("en-IN")}` : "-"}
                            </td>
                          )}
                          {canShowInvoiceAmount && (
                            <td style={td(line, "center")}>
                              ₹{" "}
                              {Number(editIncome.amount || 0).toLocaleString(
                                "en-IN",
                              )}
                            </td>
                          )}
                          {hasActionColumn && (
                            <td style={td(line, "center")}>
                              {canEditIncome && (
                                <button
                                  style={saveBtn}
                                  disabled={savingEdit}
                                  onClick={() => saveEdit(r._id)}
                                >
                                  {savingEdit ? "Saving..." : "Save"}
                                </button>
                              )}
                              <button
                                style={cancelBtn}
                                disabled={savingEdit}
                                onClick={cancelEdit}
                              >
                                Cancel
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    }

                    const taxable = Number(r.taxableValue || 0);
                    const gstAmt = Number(r.gstAmount || 0);
                    const invAmt = Number(r.invoiceAmount || r.amount || 0);

                    return (
                      <tr key={r._id}>
                        <td style={td(line, "center")}>{idx + 1}</td>
                        <td style={td(line, "center")}>
                          {r.date
                            ? new Date(r.date).toLocaleDateString("en-GB")
                            : "-"}
                        </td>
                        <td style={td(line, "center")}>
                          ₹ {Number(r.amount || 0).toLocaleString("en-IN")}
                        </td>
                        <td style={td(line, "center")}>{r.method || "-"}</td>
                        <td style={td(line, "center")}>{r.invoiceNo || "-"}</td>

                        <td style={td(line, "center")}>
                          {existingProofs.length
                            ? existingProofs.map((proof, index) => (
                                <a
                                  key={proof}
                                  href={getProofUrl(proof)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ ...proofBtnStyle, margin: 3 }}
                                >
                                  View Proof {index + 1}
                                </a>
                              ))
                            : "-"}
                        </td>

                        {canShowTaxableValue && (
                          <td style={td(line, "center")}>
                            {taxable
                              ? `₹ ${taxable.toLocaleString("en-IN")}`
                              : "-"}
                          </td>
                        )}
                        {canShowGST && (
                          <td
                            style={{
                              ...td(line, "center"),
                              whiteSpace: "nowrap",
                            }}
                          >
                            {gstAmt
                              ? `₹ ${gstAmt.toLocaleString("en-IN")}`
                              : "-"}
                          </td>
                        )}
                        {canShowInvoiceAmount && (
                          <td style={td(line, "center")}>
                            ₹ {invAmt.toLocaleString("en-IN")}
                          </td>
                        )}
                        {hasActionColumn && (
                          <td style={td(line, "center")}>
                            {canEditIncome && (
                              <button
                                style={iconBtn}
                                onClick={() => beginEdit(r)}
                              >
                                ✏️
                              </button>
                            )}

                            {canDeleteIncome && (
                              <button
                                style={delBtn}
                                onClick={() => removeIncome(r._id)}
                              >
                                🗑️
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}

                  <tr style={{ background: "#ccf2cc" }}>
                    <td style={td(line, "center")} colSpan={2}>
                      <b>TOTAL RECEIVED</b>
                    </td>
                    <td style={td(line, "center")}>
                      <b>₹ {totals.received.toLocaleString("en-IN")}</b>
                    </td>
                    {canShowPendingClient && (
                      <>
                        <td
                          style={td(line, "center")}
                          colSpan={Math.max(incomeColumnCount - 4, 1)}
                        >
                          <b>PENDING FROM CLIENT</b>
                        </td>
                        <td
                          style={{
                            ...td(line, "center"),
                            whiteSpace: "nowrap",
                          }}
                        >
                          <b>
                            ₹ {totals.pendingFromClient.toLocaleString("en-IN")}
                          </b>
                        </td>
                      </>
                    )}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </Block>
      )}

      {canSeeExpenses && canShowExpenseTable && (
        <Block
          title="FINAL PAID EXPENSES"
          color="#FFCCCB"
          titleColor="#000"
          borderColor="#ccf2cc"
        >
          <div
            style={{
              background: "#FFCCCB",
              padding: 12,
              borderRadius: 6,
              fontSize: 18,
              lineHeight: 1.6,
            }}
          >
            <div style={scrollWrap(1080)}>
              <table
                style={{
                  ...tableStyle(1080),
                  marginTop: 10,
                  fontSize: 18,
                  textAlign: "center",
                  backgroundColor: "#FFCCCB",
                  color: "#000",
                }}
              >
                <thead>
                  <tr style={{ background: "#FFCCCB", color: "#000" }}>
                    {[
                      "SR NO",
                      "DATE",
                      "VENDOR CODE",
                      "VENDOR NAME",
                      "TYPE OF WORK",
                      ...(canShowVendorPayment ? ["PAYMENT AMT"] : []),
                      "MODE OF PAYMENT",
                      "GST INVOICE",
                      "PAID BY",
                      "PAYMENT PROOF",
                    ].map((h) => (
                      <th key={h} style={{ ...th(line), textAlign: "center" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {paidExpenseRows.map((r, idx) => (
                    <tr key={r._id || idx}>
                      <td style={td(line, "center")}>{idx + 1}</td>
                      <td style={td(line, "center")}>{r.date || "-"}</td>
                      <td style={td(line, "center")}>{r.vendorCode || "-"}</td>
                      <td style={td(line, "center")}>{r.vendorName || "-"}</td>
                      <td style={td(line, "center")}>{r.workType || "-"}</td>
                      {canShowVendorPayment && (
                        <td style={td(line, "center")}>
                          ₹ {Number(r.paymentAmt || 0).toLocaleString("en-IN")}
                        </td>
                      )}
                      <td style={td(line, "center")}>{r.mode || "-"}</td>
                      <td style={td(line, "center")}>
                        {String(r.gstInvoice || "NO")}
                      </td>
                      <td style={td(line, "center")}>{r.paidBy || "-"}</td>
                      <td style={td(line, "center")}>
                        {r.paymentProof || r.proof || r.screenshot ? (
                          <a
                            href={getProofUrl(
                              r.paymentProof || r.proof || r.screenshot,
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={proofBtnStyle}
                          >
                            View Proof
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))}

                  {canShowVendorPayment && (
                    <tr style={{ background: "#f7fbff" }}>
                      <td style={td(line, "center")} colSpan={5}>
                        <b>TOTAL EXPENSES</b>
                      </td>
                      <td style={td(line, "center")}>
                        <b>₹ {totals.spent.toLocaleString("en-IN")}</b>
                      </td>
                      <td style={td(line, "center")} colSpan={4}></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Block>
      )}

      <div
        style={{
          display: "flex",
          gap: 10,
          justifyContent: "flex-end",
          marginTop: 18,
        }}
      >
        <button
          onClick={() => navigate("/project")}
          style={{
            background: "#2d6cdf",
            color: "#fff",
            border: "none",
            padding: "12px 18px",
            borderRadius: 8,
            cursor: "pointer",
            fontWeight: 700,
            fontSize: 15,
          }}
        >
          ← Back
        </button>
      </div>
    </div>
  );
}

const proofBtnStyle = {
  display: "inline-block",
  padding: "5px 10px",
  borderRadius: 6,
  border: "1px solid #93c5fd",
  background: "#eff6ff",
  color: "#1d4ed8",
  textDecoration: "none",
  fontWeight: 800,
  fontSize: 13,
  whiteSpace: "nowrap",
};

function Block({
  title,
  color,
  titleColor = "#ffffff",
  borderColor = color,
  children,
  style,
  headerStyle,
  bodyStyle,
}) {
  return (
    <div
      style={{
        border: `2px solid ${borderColor}`,
        borderRadius: 10,
        background: "#fff",
        padding: 16,
        marginBottom: 18,
        boxShadow: "0 3px 10px rgba(0,0,0,0.04)",
        ...style,
      }}
    >
      <div
        style={{
          display: "block",
          textAlign: "center",
          background: color,
          color: titleColor,
          padding: "10px 12px",
          borderRadius: 6,
          fontWeight: 800,
          letterSpacing: 0.3,
          marginBottom: 12,
          fontSize: 20,
          ...headerStyle,
        }}
      >
        {title}
      </div>
      <div style={{ ...bodyStyle }}>{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", readOnly }) {
  return (
    <div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 800,
          color: "#274690",
          marginBottom: 6,
          textAlign: "center",
        }}
      >
        {label}
      </div>
      <input
        type={type}
        readOnly={readOnly}
        value={value}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        style={{
          width: "100%",
          background: readOnly ? "#f5f7ff" : "#fff",
          border: "1px solid #cfe0ff",
          borderRadius: 8,
          padding: "11px 12px",
          outline: "none",
          fontSize: 16,
        }}
      />
    </div>
  );
}

function CheckboxField({ label, checked, onChange, disabled = false }) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: 6,
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        background: "#fff",
        height: 36,
        opacity: disabled ? 0.7 : 1,
      }}
    >
      <input
        type="checkbox"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{
          width: 18,
          height: 18,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      />
      <span style={{ fontWeight: 700, color: "#274690", fontSize: 14 }}>
        {label}
      </span>
    </label>
  );
}

function Input({ placeholder, type = "text", value, onChange, style }) {
  return (
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ ...inpBox, ...(style || {}) }}
    />
  );
}

function Select({
  value,
  onChange,
  options,
  placeholder = "Select",
  style,
  disabled = false,
}) {
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      style={{
        ...inpBox,
        ...(style || {}),
        background: disabled ? "#f5f7ff" : inpBox.background,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}

const labelStyle = {
  display: "block",
  fontSize: 14,
  fontWeight: 700,
  marginBottom: 4,
};

const readOnlyInput = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: 4,
  border: "1px solid #ccc",
  fontSize: 18,
  fontWeight: 600,
  background: "#f9f9f9",
};

const inpBox = {
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  border: "1px solid #cfe0ff",
  borderRadius: 8,
  padding: "10px 12px",
  background: "#fff",
  fontSize: 16,
};

const btnBlue = {
  background: "#2d6cdf",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  padding: "12px 16px",
  fontWeight: 800,
  cursor: "pointer",
  fontSize: 16,
};

const addBtn = {
  backgroundColor: "#ccf2cc",
  color: "#000",
  border: "2px solid #ccf2cc",
  boxShadow: "0 0 0 2px green, 0 1px 2px rgba(0,0,0,.08)",
  borderRadius: 8,
  fontWeight: 800,
  cursor: "pointer",
  fontSize: 18,
  height: 40,
  padding: "0 14px",
};

const iconBtn = {
  background: "#eef3ff",
  border: "1px solid #cfe0ff",
  color: "#274690",
  padding: "7px 12px",
  borderRadius: 6,
  cursor: "pointer",
  marginRight: 6,
  fontSize: 15,
  fontWeight: 700,
};

const delBtn = {
  ...iconBtn,
  background: "#ffeef0",
  borderColor: "#ffccd1",
  color: "#b4232a",
};

const saveBtn = {
  ...iconBtn,
  background: "#e9fbef",
  borderColor: "#b9ecc6",
  color: "#166534",
};

const cancelBtn = {
  ...iconBtn,
  background: "#fff7ed",
  borderColor: "#ffdec2",
  color: "#92400e",
};

const th = (line) => ({
  border: line,
  padding: "12px 10px",
  fontSize: 18,
  color: "black",
  textAlign: "center",
  fontWeight: 900,
});

const td = (line, align = "left") => ({
  border: line,
  padding: "10px 10px",
  fontSize: 18,
  textAlign: align,
});

const timelineCard = {
  border: "2px solid #4d7bd6",
  borderRadius: 10,
  background: "#fff",
  padding: 12,
  marginBottom: 18,
  boxShadow: "0 3px 10px rgba(0,0,0,0.04)",
};

const sectionTitle = {
  fontSize: 20,
  fontWeight: 900,
  textAlign: "center",
  color: "#0f172a",
  marginBottom: 8,
};

const barBg = {
  width: "100%",
  height: 12,
  background: "#e8eefc",
  borderRadius: 8,
  overflow: "hidden",
};

const clampPct = (n) => Math.max(0, Math.min(100, Math.round(n)));

const barFillBlue = (percent) => ({
  width: `${clampPct(percent)}%`,
  height: "100%",
  background: "#2563eb",
  borderRadius: 8,
  transition: "width 300ms ease",
});

const barFillOrange = (percent) => ({
  width: `${clampPct(percent)}%`,
  height: "100%",
  background: "#ea580c",
  borderRadius: 8,
  transition: "width 300ms ease",
});

const metaRow3 = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr",
  alignItems: "center",
  gap: 12,
  marginBottom: 6,
};

const metaItem = { fontSize: 18, lineHeight: 1.4 };
